import type { PrintOptions } from './types';

/**
 * Opens the browser print dialog scoped to the given `element` by cloning it
 * into a hidden iframe. The host page is not affected.
 *
 * Resolves once the print dialog is closed (or dismissed).
 */
export function printElement(
  element: HTMLElement,
  options: PrintOptions = {},
): Promise<void> {
  return new Promise((resolve) => {
    const { title, styles, onBeforePrint, onAfterPrint } = options;

    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(iframe);

    const iframeWindow = iframe.contentWindow;
    const iframeDoc = iframe.contentDocument || iframeWindow?.document;

    if (!iframeDoc || !iframeWindow) {
      document.body.removeChild(iframe);
      onAfterPrint?.();
      resolve();
      return;
    }

    iframeDoc.open();
    iframeDoc.write('<!DOCTYPE html><html><head></head><body></body></html>');
    iframeDoc.close();

    // Copy all parent stylesheets into the iframe
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        if (sheet.href) {
          const link = iframeDoc.createElement('link');
          link.rel = 'stylesheet';
          link.href = sheet.href;
          iframeDoc.head.appendChild(link);
        } else {
          const style = iframeDoc.createElement('style');
          const rules = Array.from(sheet.cssRules)
            .map((r) => r.cssText)
            .join('\n');
          style.textContent = rules;
          iframeDoc.head.appendChild(style);
        }
      } catch {
        // Cross-origin stylesheets may throw on cssRules access — skip them
      }
    });

    if (styles) {
      const style = iframeDoc.createElement('style');
      style.textContent = styles;
      iframeDoc.head.appendChild(style);
    }

    if (title) {
      const titleEl = iframeDoc.createElement('title');
      titleEl.textContent = title;
      iframeDoc.head.appendChild(titleEl);
    }

    iframeDoc.body.appendChild(element.cloneNode(true));
    const sourceCanvases = [
      ...(element instanceof HTMLCanvasElement ? [element] : []),
      ...element.querySelectorAll('canvas'),
    ];
    const clonedCanvases = iframeDoc.querySelectorAll('canvas');
    sourceCanvases.forEach((canvas, index) => {
      if (canvas.width === 0 || canvas.height === 0) return;
      clonedCanvases[index]?.getContext('2d')?.drawImage(canvas, 0, 0);
    });

    const FALLBACK_TIMEOUT_MS = 5000;
    const STYLESHEET_LOAD_DELAY_MS = 500;

    function cleanup() {
      document.body.removeChild(iframe);
      onAfterPrint?.();
      resolve();
    }

    // Fallback: if onafterprint never fires (some browsers), clean up after a
    // short delay so the promise doesn't hang.
    const fallbackTimer = setTimeout(() => {
      if (document.body.contains(iframe)) {
        cleanup();
      }
    }, FALLBACK_TIMEOUT_MS);

    iframeWindow.onafterprint = () => {
      clearTimeout(fallbackTimer);
      cleanup();
    };

    onBeforePrint?.();

    // Defer focus so stylesheets can finish loading
    setTimeout(() => {
      iframeWindow.focus();
      iframeWindow.print();
    }, STYLESHEET_LOAD_DELAY_MS);
  });
}
